import type { RateAggregate } from '../../../../smart/production-performance/rates-cache';
import { MIN_BASELINE_RUNS } from '../../../../smart/production-performance/performance-flags';

// The rate cache moved to smart/production-performance so the upsert form's
// Rendimiento tab can share it without a form importing from pages. Re-exported
// here so this module stays the planning board's single entry point.
export { useRatesCache } from '../../../../smart/production-performance/rates-cache';
export type {
    RateAggregate,
    RatePair,
} from '../../../../smart/production-performance/rates-cache';

export interface Prediction {
    value: number | null;
    runCount: number;
}

// Where a resolved rate came from. The distinction is shown in the UI because
// a machine average standing in for a product is a materially weaker number.
export type RateSource = 'product' | 'machine' | 'none';

export interface ResolvedRate extends Prediction {
    source: RateSource;
    // How many runs this product has on this machine, regardless of which
    // source won. Needed because the machine average now stands in for two
    // different situations — no history at all, and history too thin to trust —
    // and the row tooltip must not claim the first when it is the second.
    productRunCount: number;
}

// How few runs of its own a product may have before the machine average stands
// in. Shared with the Producción flags on purpose: one number decides what
// counts as history, so the two pages can never disagree about it.
//
// One difference in how it applies. The flags subtract the run being graded
// before counting, because a run must not sit inside the average that judges
// it; a prediction is about a run that does not exist yet, so nothing is
// subtracted and the raw window count is compared directly. The flags therefore
// need 11 recorded runs to grade the 11th, while planning trusts a product from
// its 10th onward.
export const MIN_PRODUCT_RUNS = MIN_BASELINE_RUNS;

// One window: every hourly run since the epoch (see rates-cache). The rolling
// 12-month "recent" window this used to prefer was dropped 2026-08-11 — both
// covered identical runs while hourly data starts at the epoch. Removing it also
// moves the planning board's prediction onto that single window; deliberate, and
// a no-op today because the two windows agreed.
export function predictKgHr(rate: RateAggregate): Prediction {
    return {
        value: rate.all_hours > 0 ? rate.all_kilos / rate.all_hours : null,
        runCount: rate.all_runs,
    };
}

// Whether a rate can be used yet, and if not, why not. A missing aggregate is
// still pending; an empty aggregate is a real "no history" answer.
export type RateStatus =
    | 'ready'
    | 'pending'
    | 'none';

export interface RateState extends ResolvedRate {
    status: RateStatus;
}

const PENDING_RATE: RateState = {
    status: 'pending',
    value: null,
    source: 'none',
    runCount: 0,
    productRunCount: 0,
};

// THE rule for reading a rate out of the cache. Every prediction, collapsed
// summary, coverage total, and machine picker uses this same product-first,
// machine-average-fallback decision.
export function resolveRateState({
    productId,
    productRate,
    machineRate,
}: {
    productId: number | null;
    productRate: RateAggregate | undefined;
    machineRate: RateAggregate | undefined;
}): RateState {
    if (machineRate === undefined) return PENDING_RATE;
    if (productId !== null && productRate === undefined) return PENDING_RATE;

    const resolved = resolveRate(productRate, machineRate);
    return {
        ...resolved,
        status: resolved.value === null ? 'none' : 'ready',
    };
}

/**
 * Use the product's own history when it has a usable rate AND enough runs
 * behind it; otherwise use the machine's overall average. The caller has
 * already established that all required aggregates have answered.
 *
 * The run-count condition is the part added on 2026-08-07. Before it, the
 * machine only stood in when the product had *no* usable rate at all, so a
 * product with two runs on a machine was predicted from those two runs and
 * presented exactly like one with two hundred.
 *
 * It is a real trade rather than a strict improvement, which is why it is
 * spelled out here: a handful of runs of the actual product is **unbiased but
 * noisy**, while the machine average is **systematically biased** for that
 * product, since a different calibre and width run at a genuinely different
 * kg/hr. Below MIN_PRODUCT_RUNS the noise is judged to dominate; above it the
 * product's own history wins, because product identity is what a forecast is
 * really about.
 *
 * Two guards, both mirroring the Producción flags:
 *  - the machine is taken only when it is genuinely wider, so a thin machine
 *    aggregate can never replace a better product one;
 *  - a thin product rate is still used when nothing better exists — falling
 *    all the way to `none` would blank a prediction the planner can make.
 */
function resolveRate(
    productRate: RateAggregate | undefined,
    machineRate: RateAggregate | undefined,
): ResolvedRate {
    const predicted = productRate ? predictKgHr(productRate) : null;
    // A rate with no hours behind it is not a rate, whatever its run count
    // says — so the runs only count once the product actually yields a value.
    const product =
        predicted && predicted.value !== null ? predicted : null;
    const productRunCount = product?.runCount ?? 0;

    if (product && productRunCount >= MIN_PRODUCT_RUNS) {
        return { ...product, source: 'product', productRunCount };
    }

    if (machineRate) {
        const machine = predictKgHr(machineRate);
        if (machine.value !== null && machine.runCount > productRunCount) {
            return { ...machine, source: 'machine', productRunCount };
        }
    }

    // Thin, but the only thing there is. Better than reporting no rate at all.
    if (product) {
        return { ...product, source: 'product', productRunCount };
    }

    return { value: null, runCount: 0, source: 'none', productRunCount: 0 };
}
